import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import DeactivateUserModal from "./DeactivateUserModal";
import UserViewModal from "./UserViewModal";
import UserEditModal from "./UserEditModal";

export default function UsersAdminList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("1");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });

  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("DESC");

  const [viewOpen, setViewOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [deactOpen, setDeactOpen] = useState(false);
  const [deactUser, setDeactUser] = useState(null);
  const [deactLoading, setDeactLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const fetchUsers = async (p = page) => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/users", {
        params: {
          search: search || undefined,
          status: status || undefined,
          page: p,
          limit,
          sortBy,
          order,
        },
      });

      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: p, limit, totalPages: 1 });
      setPage(p);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load users";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1);
  }, []);

  const onApply = (e) => {
    e.preventDefault();
    fetchUsers(1);
  };

  const toggleSortCreated = () => {
    setSortBy("created_at");
    setOrder((o) => (o === "DESC" ? "ASC" : "DESC"));
    setTimeout(() => fetchUsers(1), 0);
  };

  const openView = async (user_id) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewUser(null);
    try {
      const res = await api.get(`/admin/users/${user_id}`);
      console.log("setViewUser", res.data?.data);
      setViewUser(res.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load user");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const openDeactivate = (u) => {
    setDeactUser(u);
    setDeactOpen(true);
  };

  const onDeactivateConfirm = async (reason) => {
    if (!deactUser?.user_id) return;
    setDeactLoading(true);
    try {
      await api.patch(`/admin/users/${deactUser.user_id}/deactivate`, { reason });
      toast.success("User deactivated");
      setDeactOpen(false);
      setDeactUser(null);
      fetchUsers(page);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Deactivate failed");
    } finally {
      setDeactLoading(false);
    }
  };

  const openEdit = async (user_id) => {
    setEditOpen(true);
    setEditLoading(true);
    setEditUser(null);

    try {
      const res = await api.get(`/admin/users/${user_id}/edit`);
      setEditUser(res.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load user");
      setEditOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const onUpdateConfirm = async (payload) => {
    if (!editUser?.user_id) return;

    setEditSaving(true);
    try {
      await api.put(`/admin/users/${editUser.user_id}`, payload);
      toast.success("User updated successfully");
      setEditOpen(false);
      setEditUser(null);
      fetchUsers(page);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
        </div>
      </div>

      <form onSubmit={onApply} className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:min-w-[260px] sm:flex-1">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Name / Email / Mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:min-w-[200px] sm:w-auto">
          <label className="text-sm font-semibold">Status</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="1">Active</option>
            {/* <option value="0">Inactive</option>
            <option value="deleted">Deleted</option> */}
          </select>
        </div>

        <button type="submit" className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90">
          Apply
        </button>
      </form>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex items-center justify-between">
          <span>User List</span>

          <button
            type="button"
            onClick={toggleSortCreated}
            className="text-xs font-semibold rounded-lg border px-3 py-1 hover:bg-white"
          >
            Sort Created: {order}
          </button>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="border-t">
              {rows.map((u) => (
                <tr key={u.user_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">
                    {[u.first_name, u.last_name].filter(Boolean).join(" ") || "-"}
                  </td>
                  <td className="px-4 py-3">{u.email || "-"}</td>
                  <td className="px-4 py-3">{u.mobile_no || "-"}</td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                        Number(u.is_delete) === 1
                          ? "bg-red-100 text-red-700"
                          : Number(u.status) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {Number(u.is_delete) === 1 ? "Deleted" : Number(u.status) === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex gap-2 items-center flex-wrap">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openView(u.user_id)}
                      >
                        View
                      </button>

                      {/* <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openEdit(u.user_id)}
                      >
                        Edit
                      </button> */}

                      {Number(u.is_delete) === 1 ? null : Number(u.status) === 1 ? (
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => openDeactivate(u)}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500">Reason: {u.reason || "-"}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          disabled={page <= 1 || loading}
          onClick={() => fetchUsers(page - 1)}
          className="rounded-lg border px-3 py-1 text-sm font-semibold disabled:opacity-50"
        >
          Prev
        </button>

        <div className="text-sm text-gray-700">
          Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: {meta.total})
        </div>

        <button
          type="button"
          disabled={page >= meta.totalPages || loading}
          onClick={() => fetchUsers(page + 1)}
          className="rounded-lg border px-3 py-1 text-sm font-semibold disabled:opacity-50"
        >
          Next
        </button>
      </div>

      <UserViewModal open={viewOpen} onClose={() => setViewOpen(false)} loading={viewLoading} user={viewUser} />

      <UserEditModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditUser(null);
        }}
        loading={editLoading}
        saving={editSaving}
        user={editUser}
        onConfirm={onUpdateConfirm}
      />

      <DeactivateUserModal
        open={deactOpen}
        onClose={() => {
          setDeactOpen(false);
          setDeactUser(null);
        }}
        loading={deactLoading}
        user={deactUser}
        onConfirm={onDeactivateConfirm}
      />
    </div>
  );
}
